import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name, size = 28 }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function AssignmentSubmissionReport() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') {
        for (const th of theads) { th.classList.remove('table-secondary'); th.classList.add('table-primary'); }
        t.classList.add('table-dark');
      } else {
        t.classList.remove('table-dark');
        for (const th of theads) { th.classList.remove('table-primary'); th.classList.add('table-secondary'); }
      }
    }
  };
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId]     = useState(null);
  const [semester, setSemester] = useState('all');   // 'all' | '1' | '2'
  const [rows, setRows]         = useState([]);
  const [search, setSearch]     = useState('');
  const [activeTab, setActiveTab] = useState('byRoom'); // 'byRoom' | 'byStudent'
  const [expandedRooms, setExpandedRooms]       = useState(() => new Set());
  const [expandedSubjects, setExpandedSubjects] = useState(() => new Set());

  const toggleRoom = (id) => {
    setExpandedRooms(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSubject = (id) => {
    setExpandedSubjects(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Load study years, default to latest (list is ordered DESC)
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  // Fetch report on year / semester change
  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    const sem = semester === 'all' ? '' : `/${semester}`;
    axiosInstance.get(`${API}/getAssignmentSubmissionByYear/${yearId}${sem}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, semester]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = search.trim().toLowerCase();

  // Rows filtered by the shared student id/name search
  const filteredRows = useMemo(() => (
    query
      ? rows.filter(r =>
          String(r.st_idnumber || '').toLowerCase().includes(query) ||
          String(r.st_name || '').toLowerCase().includes(query))
      : rows
  ), [rows, query]);

  // Group room → subject → student (by-room tab)
  const rooms = useMemo(() => {
    const roomMap = new Map();
    filteredRows.forEach(r => {
      if (!roomMap.has(r.r_id)) {
        roomMap.set(r.r_id, {
          r_id: r.r_id,
          class_room: r.class_room,
          p_code: r.p_code,
          subjectMap: new Map(),
          studentIds: new Set(),
        });
      }
      const room = roomMap.get(r.r_id);
      room.studentIds.add(r.st_id);

      if (!room.subjectMap.has(r.teach_id)) {
        room.subjectMap.set(r.teach_id, {
          teach_id: r.teach_id,
          sub_subid: r.sub_subid,
          sub_subname: r.sub_subname,
          sub_type: r.sub_type,
          sub_semester: r.sub_semester,
          t_name: r.t_name,
          students: [],
        });
      }
      room.subjectMap.get(r.teach_id).students.push({
        st_id: r.st_id,
        st_idnumber: r.st_idnumber,
        st_name: r.st_name,
        st_photo: r.st_photo,
        submitted_items: Number(r.submitted_items || 0),
        due_items: Number(r.due_items || 0),
      });
    });

    return [...roomMap.values()].map(room => ({
      r_id: room.r_id,
      class_room: room.class_room,
      p_code: room.p_code,
      studentCount: room.studentIds.size,
      subjects: [...room.subjectMap.values()],
    }));
  }, [filteredRows]);

  // Group by student (by-student tab): each subject with pending work + missing count
  const students = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      if (!map.has(r.st_id)) {
        map.set(r.st_id, {
          st_id: r.st_id,
          st_idnumber: r.st_idnumber,
          st_name: r.st_name,
          st_photo: r.st_photo,
          class_room: r.class_room,
          p_code: r.p_code,
          subjects: [],
          totalMissing: 0,
        });
      }
      const s = map.get(r.st_id);
      const due = Number(r.due_items || 0);
      const submitted = Number(r.submitted_items || 0);
      const missing = due - submitted;
      s.subjects.push({
        teach_id: r.teach_id,
        sub_subid: r.sub_subid,
        sub_subname: r.sub_subname,
        sub_type: r.sub_type,
        sub_semester: r.sub_semester,
        t_name: r.t_name,
        missing,
        submitted_items: submitted,
        due_items: due,
      });
      s.totalMissing += missing;
    });
    return [...map.values()]
      .map(s => ({ ...s, subjects: s.subjects.sort((a, b) => b.missing - a.missing) }))
      .sort((a, b) => b.totalMissing - a.totalMissing);
  }, [filteredRows]);

  // KPI: rooms / subjects / distinct students with pending work
  const kpi = useMemo(() => {
    const studentSet = new Set();
    let subjectCount = 0;
    rooms.forEach(room => {
      subjectCount += room.subjects.length;
      room.subjects.forEach(s => s.students.forEach(st => studentSet.add(st.st_id)));
    });
    return { rooms: rooms.length, subjects: subjectCount, students: studentSet.size };
  }, [rooms]);

  const kpiCards = [
    { label: 'ห้องที่มีงานค้าง',   value: kpi.rooms,    color: 'primary', icon: 'ri-government-line', unit: 'ห้อง' },
    { label: 'วิชาที่มีงานค้าง',   value: kpi.subjects, color: 'warning', icon: 'ri-book-2-line',    unit: 'วิชา' },
    { label: 'นักเรียนที่ส่งไม่ครบ', value: kpi.students, color: 'danger',  icon: 'ri-user-follow-line', unit: 'คน' },
  ];

  // When searching, auto-expand everything with results (by-room tab)
  const searching = query.length > 0;
  const isRoomOpen = (id) => searching || expandedRooms.has(id);
  const isSubjectOpen = (id) => searching || expandedSubjects.has(id);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการสามัญ</Link></li>
              <li className="breadcrumb-item active">รายงานการส่งงาน</li>
            </ol>
            <h4 className="main-title mb-0">รายงานการส่งงาน</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {/* Year filter */}
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 150 }}
              value={yearId || ''}
              onChange={e => setYearId(e.target.value ? Number(e.target.value) : null)}
            >
              {yearList.map(y => (
                <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
              ))}
            </select>

            {/* Semester filter */}
            <div className="btn-group btn-group-sm">
              {[['all','ทุกภาคเรียน'],['1','ภาคเรียน 1'],['2','ภาคเรียน 2']].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${semester === v ? '' : '-outline'}-primary`}
                  onClick={() => setSemester(v)}
                >{label}</button>
              ))}
            </div>

            {/* Search by student id / name (shared across tabs) */}
            <div style={{ minWidth: 220 }}>
              <Form.Control
                type="text"
                size="sm"
                placeholder="ค้นหาเลขประจำตัว / ชื่อนักเรียน"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={k => k && setActiveTab(k)}>
          <Nav.Item>
            <Nav.Link eventKey="byRoom"><i className="ri-government-line me-1"></i>ตามห้องเรียน</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="byStudent"><i className="ri-user-search-line me-1"></i>ตามรายนักเรียน</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'byRoom' ? (
          <React.Fragment>
            {/* KPI Cards */}
            <Row className="g-3 mb-4">
              {kpiCards.map(k => (
                <Col key={k.label} xs={12} md={4}>
                  <Card className={`card-one border-${k.color} border-start border-4`}>
                    <Card.Body className="p-3">
                      <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                      <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                      <h3 className={`fw-bold text-${k.color} mb-0`}>{k.value} <span className="fs-sm fw-normal text-muted">{k.unit}</span></h3>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Drill-down: room → subject → student */}
            {rooms.length === 0 ? (
              <Card className="card-one">
                <Card.Body className="text-center text-muted py-5">
                  <i className="ri-checkbox-circle-line fs-1 text-success d-block mb-2"></i>
                  {search.trim() ? 'ไม่พบนักเรียนตามคำค้นหา' : 'นักเรียนส่งงานครบทุกคน ไม่มีข้อมูลค้าง'}
                </Card.Body>
              </Card>
            ) : (
              rooms.map(room => (
                <Card className="card-one mb-3" key={room.r_id}>
                  <Card.Header
                    className="d-flex align-items-center gap-2 cursor-pointer"
                    role="button"
                    onClick={() => toggleRoom(room.r_id)}
                  >
                    <i className={`ri-arrow-${isRoomOpen(room.r_id) ? 'down' : 'right'}-s-line fs-4`}></i>
                    <Card.Title as="h6" className="mb-0">
                      ห้อง ม.{room.class_room} {room.p_code && <span className="text-muted">({room.p_code})</span>}
                    </Card.Title>
                    <Badge bg="secondary" className="ms-2">{room.subjects.length} วิชา</Badge>
                    <Badge bg="danger" className="ms-auto">{room.studentCount} คนที่ค้าง</Badge>
                  </Card.Header>

                  {isRoomOpen(room.r_id) && (
                    <Card.Body className="p-0">
                      {room.subjects.map(subj => (
                        <div key={subj.teach_id} className="border-bottom">
                          <div
                            className="d-flex align-items-center gap-2 px-3 py-2 bg-light cursor-pointer"
                            role="button"
                            onClick={() => toggleSubject(subj.teach_id)}
                          >
                            <i className={`ri-arrow-${isSubjectOpen(subj.teach_id) ? 'down' : 'right'}-s-line`}></i>
                            <span className="fw-semibold fs-sm">
                              <span className="text-muted">{subj.sub_subid}</span> {subj.sub_subname}
                              {subj.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                            </span>
                            <span className="text-muted fs-xs">— {subj.t_name}</span>
                            <Badge bg="warning" className="ms-auto">{subj.students.length} คน</Badge>
                          </div>

                          {isSubjectOpen(subj.teach_id) && (
                            <Table responsive hover size="sm" className="mb-0">
                              <thead className="table-secondary">
                                <tr>
                                  <th className="text-center" style={{ width: 50 }}>#</th>
                                  <th style={{ width: 60 }}></th>
                                  <th style={{ width: 140 }}>เลขประจำตัว</th>
                                  <th>ชื่อ - สกุล</th>
                                  <th className="text-center" style={{ width: 150 }}>ส่งแล้ว (ถึงกำหนด)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subj.students.map((st, i) => (
                                  <tr key={st.st_id}>
                                    <td className="text-center">{i + 1}</td>
                                    <td className="text-center"><StudentAvatar photo={st.st_photo} name={st.st_name} /></td>
                                    <td className="fs-sm">{st.st_idnumber}</td>
                                    <td className="fs-sm">{st.st_name}</td>
                                    <td className="text-center">
                                      <Badge bg={st.submitted_items === 0 ? 'danger' : 'warning'} className="fs-xs">
                                        {st.submitted_items}/{st.due_items}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      ))}
                    </Card.Body>
                  )}
                </Card>
              ))
            )}
          </React.Fragment>
        ) : (
          /* By-student tab: search a student → show profile, photo, total pending & per-subject breakdown */
          <React.Fragment>
            {!search.trim() ? (
              <Card className="card-one">
                <Card.Body className="text-center text-muted py-5">
                  <i className="ri-user-search-line fs-1 d-block mb-2"></i>
                  ป้อนเลขประจำตัวหรือชื่อนักเรียนในช่องค้นหาด้านบน เพื่อดูงานที่ค้างของนักเรียน
                </Card.Body>
              </Card>
            ) : students.length === 0 ? (
              <Card className="card-one">
                <Card.Body className="text-center text-muted py-5">
                  <i className="ri-checkbox-circle-line fs-1 text-success d-block mb-2"></i>
                  ไม่พบนักเรียนที่ค้างงานตามคำค้นหา
                </Card.Body>
              </Card>
            ) : (
              students.map(st => (
                <Card className="card-one mb-3" key={st.st_id}>
                  <Card.Body>
                    {/* Student profile summary */}
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                      <StudentAvatar photo={st.st_photo} name={st.st_name} size={64} />
                      <div className="flex-grow-1">
                        <h5 className="mb-1">{st.st_name}</h5>
                        <div className="fs-sm text-muted">
                          <span className="me-3"><i className="ri-profile-line me-1"></i>เลขประจำตัว {st.st_idnumber}</span>
                          <span><i className="ri-government-line me-1"></i>ห้อง ม.{st.class_room}{st.p_code && ` (${st.p_code})`}</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="display-6 fw-bold text-danger lh-1">{st.totalMissing}</div>
                        <div className="fs-xs text-muted">งานที่ค้าง · {st.subjects.length} วิชา</div>
                      </div>
                    </div>

                    {/* Per-subject breakdown */}
                    <Table responsive hover size="sm" className="mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th className="text-center" style={{ width: 50 }}>#</th>
                          <th>วิชา</th>
                          <th>ครูผู้สอน</th>
                          <th className="text-center" style={{ width: 90 }}>ภาคเรียน</th>
                          <th className="text-center" style={{ width: 90 }}>ค้าง</th>
                          <th className="text-center" style={{ width: 150 }}>ส่งแล้ว (ถึงกำหนด)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {st.subjects.map((subj, i) => (
                          <tr key={subj.teach_id}>
                            <td className="text-center">{i + 1}</td>
                            <td className="fs-sm">
                              <span className="text-muted">{subj.sub_subid}</span> {subj.sub_subname}
                              {subj.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                            </td>
                            <td className="fs-sm">{subj.t_name}</td>
                            <td className="text-center fs-sm">{subj.sub_semester}</td>
                            <td className="text-center">
                              <Badge bg="danger" className="fs-xs">{subj.missing}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg={subj.submitted_items === 0 ? 'danger' : 'warning'} className="fs-xs">
                                {subj.submitted_items}/{subj.due_items}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              ))
            )}
          </React.Fragment>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
